/**
 * useAIChat Hook
 *
 * Manages AI chat state and streaming communication with AI API proxy.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

import { useAuth } from '../providers/AuthProvider';

// AI Configuration - uses the API proxy endpoint
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const AI_CHAT_URL = `${API_BASE_URL}/ai/chat/completions`;
const AI_MODEL_NAME = import.meta.env.VITE_AI_MODEL_NAME || 'gpt-oss:20b';

const SYSTEM_PROMPT = `
You are a senior investment analyst focused on healthcare platform investments, specifically in RCM (Revenue Cycle Management), EMR (Electronic Medical Records), CM (Care Management), and adjacent healthcare IT and services sectors.

Your role is to help users analyze healthcare investment requests, business opportunities, financials, operations, and strategy.

────────────────────────────────────────
OUTPUT FORMAT & STYLE RULES (HIGHEST PRIORITY)
────────────────────────────────────────

- You MUST output responses using strictly valid Markdown.
- Your formatting must be clean, professional, and suitable for a financial analysis UI.
- Use clear section headers, bullet points, and tables where appropriate.
- Maintain a positive, helpful, and professional tone.
- Never use emojis.
- Never use slang or casual language.
- Write as a polished investment professional.

────────────────────────────────────────
CRITICAL TABLE FORMATTING RULES (ABSOLUTE REQUIREMENT)
────────────────────────────────────────

**EVERY SINGLE TABLE MUST BE IN A CODE BLOCK - NO EXCEPTIONS**

When presenting ANY data in table format with pipes (|) and rows:

1. **MANDATORY**: Wrap the ENTIRE table in triple backticks (\`\`\`)
2. Place the opening \`\`\` on its own line BEFORE the table
3. Place the closing \`\`\` on its own line AFTER the table
4. **NEVER** render pipe-separated (|) content outside code blocks
5. **NEVER** place tables inline with regular text

**CORRECT FORMAT (Always use this):**

\`\`\`
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |
\`\`\`

**WRONG - Never do this:**
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |

**Additional table rules:**
- Header row first
- Separator row using dashes (|-----|-----|)
- One data row per line
- No blank lines inside tables
- No explanatory text inside tables
- Consistent column spacing for alignment
- Concise column headers
- Right-align numeric values using spacing

**This applies to ALL tables including:**
- Financial projections
- KPI benchmarks
- Comparison matrices
- Business model breakdowns
- Market opportunity data
- Any multi-column structured data

If you cannot format data as a proper code-blocked table, use bullet points or a different format instead.

────────────────────────────────────────
SCOPE & RELEVANCE RULES
────────────────────────────────────────

You may only respond to requests related to:
- Healthcare services
- Healthcare technology
- RCM, EMR, CM platforms
- Healthcare operations
- Healthcare financials
- Healthcare investing, M&A, or private equity
- Provider, payer, or value-based care business models

If a user asks for anything outside this scope, respond with:

"This request is not relevant to the current healthcare investment analysis context."

Then end the response.

Do not provide additional commentary.

────────────────────────────────────────
ANALYST BEHAVIOR RULES
────────────────────────────────────────

- Think like a private equity investment professional
- Highlight value drivers, risks, and upside
- Be concise but thorough
- Make assumptions explicit
- Use tables for financials, KPIs, benchmarks, and comparisons
- Use bullet points for insights and recommendations

Your goal is to help investors quickly understand:
- Business model
- Market opportunity
- Financial performance
- Scalability
- Risks
- Exit potential

Always optimize for clarity, decision-making, and executive readability.

`;

/**
 * Chat message type
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

/**
 * Message format for API
 */
interface APIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * Hook return type
 */
export interface UseAIChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  sendMessage: (text: string, context?: string) => Promise<void>;
  clearChat: () => void;
  clearError: () => void;
}

const SESSION_STORAGE_KEY = 'medsuite-ai-chat-messages';

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Load messages from sessionStorage
 */
function loadMessagesFromStorage(): ChatMessage[] {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Restore Date objects from ISO strings
      return parsed.map((m: ChatMessage & { timestamp: string }) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
    }
  } catch {
    // Ignore storage errors
  }
  return [];
}

/**
 * Save messages to sessionStorage
 */
function saveMessagesToStorage(messages: ChatMessage[]): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
  }
}

/**
 * Stream chat completion from AI API proxy
 */
async function* streamChatCompletion(
  messages: APIMessage[],
  accessToken?: string
): AsyncGenerator<string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Include auth token if available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(AI_CHAT_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: AI_MODEL_NAME,
      messages: [{ role: 'system', content: SYSTEM_PROMPT }, ...messages],
      stream: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') {
            return;
          }

          try {
            const parsed = JSON.parse(data);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              yield delta;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * AI Chat hook for managing chat state and streaming
 */
export function useAIChat(): UseAIChatReturn {
  const { getAccessToken } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessagesFromStorage());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Persist messages to sessionStorage whenever they change
  useEffect(() => {
    saveMessagesToStorage(messages);
  }, [messages]);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen((prev) => !prev), []);
  const clearError = useCallback(() => setError(null), []);

  const sendMessage = useCallback(
    async (text: string, context?: string) => {
      if (!text.trim()) {
        return;
      }

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Add user message
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Create assistant placeholder for streaming
      const assistantId = generateId();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      setIsLoading(true);
      setError(null);

      try {
        // Get access token for API authentication
        const accessToken = await getAccessToken();

        // Build conversation history for API
        const conversationMessages: APIMessage[] = messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        // Add the new user message with context if available
        const userContent = context
          ? `[Context about current investment:\n${context}]\n\n${text.trim()}`
          : text.trim();

        conversationMessages.push({
          role: 'user',
          content: userContent,
        });

        // Stream response
        let fullContent = '';
        for await (const chunk of streamChatCompletion(
          conversationMessages,
          accessToken || undefined
        )) {
          fullContent += chunk;
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: fullContent } : m))
          );
        }

        // Mark streaming complete
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
        setError(errorMessage);
        // Remove failed assistant message
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [messages, getAccessToken]
  );

  const clearChat = useCallback(() => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    messages,
    isLoading,
    error,
    isOpen,
    openPanel,
    closePanel,
    togglePanel,
    sendMessage,
    clearChat,
    clearError,
  };
}

export default useAIChat;
